/**
 * src/services/spatial/mapActions.ts
 *
 * Map intent, expressed by features that do not own the camera.
 *
 * The rule this file exists to encode: a feature says *what* it wants
 * the map to show and never *where on the screen* to show it. Deciding
 * that a building deserves zoom 17 at pitch 60, that a latitude must be
 * clamped before Mapbox is handed it, and that a camera change belongs
 * on the next frame rather than mid-click — that is all one concern,
 * and it was previously inlined into a JSX callback inside the
 * inspector and duplicated as a closure inside `App`.
 *
 * That duplication was not academic. The inspector's copy carries a
 * comment about "Cannot read properties of null (reading '3')": a
 * non-finite or out-of-range coordinate makes the projection matrix
 * non-invertible and takes down the render loop. Any second caller who
 * wrote the obvious `flyTo` would rediscover that crash, because the
 * knowledge was in a callback rather than in a function.
 *
 * Built over `SpatialMapProvider`, which is already the facade over the
 * native Mapbox/MapLibre/MapTiler instance, so this is a vocabulary on
 * top of an existing abstraction rather than a second one beside it.
 *
 * Deliberately small. Intents land here when they have a caller, which
 * is why the event intents below arrive now and did not arrive
 * earlier: the event UI is the caller.
 *
 * They come in two kinds, and the difference is worth naming because
 * it is where the rule at the top gets tested.
 *
 * A CAMERA intent asks the map to look somewhere. `focusSpatialObject`
 * and `fitEventArea` are these, and they decide zoom, clamping and
 * timing here so no caller has to know that an out-of-range latitude
 * makes the projection matrix non-invertible.
 *
 * A SURFACE intent asks the map to show something — open an event,
 * highlight one. These need the map's own state, so they are handed to
 * host callbacks the way `cinematic` already is, and they return false
 * when the host gave none. That is deliberately honest: an event panel
 * asking to be opened on a map that cannot open panels should learn
 * that it did not happen, rather than believing it did.
 *
 * What a caller still may not do is compute a screen position. There
 * are no pixel arguments anywhere in this vocabulary, and the one
 * place that ranks overlapping panels is `mapSurfaces`, which does not
 * own coordinates either.
 */

import type { SpatialMapProvider } from './mapProvider';

/** Web Mercator degenerates towards the poles; Mapbox clamps here too. */
const MAX_SAFE_LATITUDE = 85;

/** Close enough to read a building, far enough to keep its context. */
const DEFAULT_OBJECT_ZOOM = 17;
const MIN_OBJECT_ZOOM = 15;
const MAX_OBJECT_ZOOM = 18;

export interface LngLat {
  lng: number;
  lat: number;
}

/**
 * The parts of an event a map can be asked to show.
 *
 * Named rather than passed as free text so a typo is a compile error
 * instead of a silently ignored intent. Each corresponds to a real
 * sub-geometry an event can carry: where the performer stands, where
 * people check in, where the broadcast reaches, and the area an
 * execution rule applies over.
 */
export type EventAreaKind = 'stage' | 'check_in' | 'broadcast' | 'execution_rule';

export const EVENT_AREA_KINDS: readonly EventAreaKind[] = [
  'stage', 'check_in', 'broadcast', 'execution_rule',
];

/** Degrees of padding around a fitted area, so it does not touch the edges. */
const FIT_PADDING_DEG = 0.0004;

/** Bounds tighter than this are a point, and fitting one is a zoom to infinity. */
const MIN_FIT_SPAN_DEG = 1e-6;

/**
 * Every usable coordinate pair in a geometry.
 *
 * The plural of `safeCenterOf`, and separate from it because fitting
 * an area needs the extent rather than a representative point. Same
 * tolerance for shape: it walks until it finds numeric pairs, so a
 * Polygon and a MultiPolygon answer the same way.
 */
function collectCoordinates(geometry: unknown): LngLat[] {
  const out: LngLat[] = [];

  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      const [lng, lat] = node as [number, number];
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        out.push({
          lng: Math.max(-180, Math.min(180, lng)),
          lat: Math.max(-MAX_SAFE_LATITUDE, Math.min(MAX_SAFE_LATITUDE, lat)),
        });
      }
      return;
    }
    node.forEach(walk);
  };

  walk((geometry as { coordinates?: unknown } | null)?.coordinates);
  return out;
}

/**
 * The box an area occupies, or null when it does not occupy one.
 *
 * A single point returns null rather than a zero-span box. Fitting a
 * box with no width is a request for infinite zoom, and the honest
 * answer for a point is to focus it instead — which is a different
 * intent that already exists.
 */
export function safeBoundsOf(geometry: unknown): {
  sw: LngLat; ne: LngLat;
} | null {
  const points = collectCoordinates(geometry);
  if (points.length === 0) return null;

  let minLng = Infinity; let minLat = Infinity;
  let maxLng = -Infinity; let maxLat = -Infinity;
  for (const p of points) {
    if (p.lng < minLng) minLng = p.lng;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lng > maxLng) maxLng = p.lng;
    if (p.lat > maxLat) maxLat = p.lat;
  }

  if (maxLng - minLng < MIN_FIT_SPAN_DEG && maxLat - minLat < MIN_FIT_SPAN_DEG) {
    return null;
  }

  return {
    sw: { lng: minLng - FIT_PADDING_DEG, lat: minLat - FIT_PADDING_DEG },
    ne: { lng: maxLng + FIT_PADDING_DEG, lat: maxLat + FIT_PADDING_DEG },
  };
}

/**
 * The first usable coordinate in a geometry, clamped into the band a
 * projection can actually handle.
 *
 * Any geometry: the walk descends coordinate arrays until it finds a
 * numeric pair, so a Point, a Polygon and a MultiPolygon all answer
 * without the caller knowing which it has.
 *
 * Returns null rather than a guess when there is nothing usable. A
 * caller that cannot find a coordinate should do nothing, not fly
 * somewhere off the coast of Africa — which is where a silently
 * defaulted zero-zero lands.
 */
export function safeCenterOf(geometry: unknown): LngLat | null {
  const found: number[] = [];

  const walk = (node: unknown): void => {
    if (found.length >= 2) return;
    if (!Array.isArray(node)) return;
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      found.push(node[0], node[1]);
      return;
    }
    node.forEach(walk);
  };

  walk((geometry as { coordinates?: unknown } | null)?.coordinates);
  if (found.length < 2) return null;

  const [lng, lat] = found;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  return {
    lng: Math.max(-180, Math.min(180, lng)),
    lat: Math.max(-MAX_SAFE_LATITUDE, Math.min(MAX_SAFE_LATITUDE, lat)),
  };
}

/**
 * A zoom that will not break the projection.
 *
 * A provider that has not finished loading reports a non-finite zoom,
 * and passing that through is one of the ways the render loop dies.
 */
export function safeObjectZoom(current: unknown): number {
  const z = Number(current);
  if (!Number.isFinite(z)) return DEFAULT_OBJECT_ZOOM - 1;
  return Math.min(Math.max(z, MIN_OBJECT_ZOOM), MAX_OBJECT_ZOOM);
}

export interface MapActionsDeps {
  provider: SpatialMapProvider;
  /**
   * The host's own camera move, when it has one.
   *
   * `App` owns a cinematic approach — pitch, bearing, 3D buildings on
   * arrival, and a cooldown that suppresses a competing auto-fit — and
   * that is camera *policy*, which belongs to whoever owns the map
   * rather than to this vocabulary. Given one, intents defer to it.
   */
  cinematic?: ((center: [number, number]) => void) | null;
  /**
   * Defers a camera change out of the current frame. Overridable so a
   * test does not need an animation clock.
   */
  schedule?: (fn: () => void) => void;
  /**
   * Open an event's own surface, when the host has one to open.
   *
   * The map layer owns which panel is showing and how it competes with
   * the hover card and the chat card, so opening one is its decision
   * and this is only the request. Absent means the intent is refused
   * rather than silently dropped.
   */
  openEvent?: ((objectId: string) => void) | null;
  /**
   * Mark an object as the one being talked about.
   *
   * Passing null clears it. The map decides what "marked" looks like;
   * a caller that wanted a specific colour would be owning
   * presentation, which is the thing this file prevents.
   */
  highlight?: ((objectId: string | null) => void) | null;
}

export interface MapActions {
  /**
   * Put a spatial object on screen.
   *
   * Takes a geometry or a coordinate — an intent, not a camera. What
   * zoom, what pitch, whether to animate and whether the coordinate is
   * even safe to project are all decided here.
   *
   * Returns whether it acted, so a caller can tell "nothing to focus"
   * from "focused", rather than assuming the map moved.
   */
  focusSpatialObject: (input: {
    geometry?: unknown;
    center?: LngLat | null;
    heightMeters?: number | null;
  }) => boolean;

  /**
   * Put a whole area on screen rather than a point in it.
   *
   * What an event wants when it is a field rather than a doorway: a
   * festival site, a check-in queue, the reach of a broadcast. Falls
   * back to focusing the middle when the shape has no extent, because
   * fitting a point is a request for infinite zoom.
   */
  fitEventArea: (input: { geometry?: unknown }) => boolean;

  /**
   * Open an event's surface.
   *
   * Returns false when the host has no surface to open, so an event
   * panel learns that nothing happened instead of assuming it did.
   */
  openEvent: (input: { objectId?: string | null }) => boolean;

  /**
   * Mark, or unmark, the object being talked about.
   *
   * Separate from focusing: a list of three events can highlight the
   * one under the cursor without dragging the camera around, and a
   * camera that moves on hover is a map nobody can read.
   */
  highlightEvent: (input: { objectId?: string | null }) => boolean;

  /**
   * Show a child event: mark it, open it, and put it on screen.
   *
   * One intent rather than three calls because "show me that session"
   * is one thing a person asked for, and a caller stitching the three
   * together would eventually stitch them differently somewhere else.
   */
  showChildEvent: (input: {
    objectId?: string | null;
    geometry?: unknown;
  }) => boolean;

  /**
   * Show a named part of an event.
   *
   * The stage, the check-in area, the broadcast reach, the area a rule
   * applies over. The kind travels with the request so the map can
   * decide how each looks; this only says which one and where it is.
   */
  showEventArea: (input: {
    kind: EventAreaKind;
    objectId?: string | null;
    geometry?: unknown;
  }) => boolean;
}

export function createMapActions(deps: MapActionsDeps): MapActions {
  const {
    provider, cinematic = null, openEvent = null, highlight = null,
  } = deps;
  const schedule = deps.schedule
    ?? ((fn: () => void) => {
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fn);
      else fn();
    });

  const actions: MapActions = {
    focusSpatialObject({ geometry, center }) {
      const target = center
        ? safeCenterOf({ coordinates: [center.lng, center.lat] })
        : safeCenterOf(geometry);
      if (!target) return false;

      schedule(() => {
        // Nothing here is allowed to take the application down. A
        // camera move failing is a visitor who stayed where they were;
        // a throw out of an animation frame is a blank page.
        try {
          if (cinematic) {
            cinematic([target.lng, target.lat]);
            return;
          }

          // No host policy, so the conservative path. A single-step
          // jump rather than an animation: Mapbox v3's flyTo can
          // compute a non-invertible matrix on an intermediate frame
          // and kill the render loop, and an object appearing
          // immediately is a smaller loss than a dead map.
          const zoom = safeObjectZoom(provider.getZoom());
          const native = provider.nativeMap as {
            jumpTo?: (o: { center: [number, number]; zoom: number }) => void;
          } | null;

          if (native && typeof native.jumpTo === 'function') {
            native.jumpTo({ center: [target.lng, target.lat], zoom });
          } else {
            provider.flyTo({ center: [target.lng, target.lat], zoom });
          }
        } catch { /* a camera that did not move is not a failed session */ }
      });

      return true;
    },

    fitEventArea({ geometry }) {
      const bounds = safeBoundsOf(geometry);
      // A shape with no extent is a point, and focusing it is the
      // right answer rather than a failure.
      if (!bounds) return actions.focusSpatialObject({ geometry });

      schedule(() => {
        try {
          const native = provider.nativeMap as {
            fitBounds?: (
              b: [[number, number], [number, number]],
              o?: { padding?: number; duration?: number },
            ) => void;
          } | null;

          if (native && typeof native.fitBounds === 'function') {
            native.fitBounds(
              [[bounds.sw.lng, bounds.sw.lat], [bounds.ne.lng, bounds.ne.lat]],
              // No animation, for the reason the focus path jumps: an
              // intermediate frame can compute a matrix that kills the
              // render loop, and arriving immediately is the smaller loss.
              { padding: 48, duration: 0 },
            );
            return;
          }

          // No native fit. Centre the box and take the conservative
          // object zoom rather than deriving one from the span — a
          // computed zoom is where an off-by-one becomes a blank map.
          const centre = {
            lng: (bounds.sw.lng + bounds.ne.lng) / 2,
            lat: (bounds.sw.lat + bounds.ne.lat) / 2,
          };
          provider.flyTo({
            center: [centre.lng, centre.lat],
            zoom: safeObjectZoom(provider.getZoom()),
          });
        } catch { /* as above: a still camera is not a dead session */ }
      });

      return true;
    },

    openEvent({ objectId }) {
      const id = typeof objectId === 'string' && objectId.trim() ? objectId.trim() : null;
      if (!id || !openEvent) return false;
      // Not scheduled: opening a panel is React state, and deferring
      // it to an animation frame would lose it if the host unmounted.
      try { openEvent(id); } catch { return false; }
      return true;
    },

    highlightEvent({ objectId }) {
      if (!highlight) return false;
      const id = typeof objectId === 'string' && objectId.trim() ? objectId.trim() : null;
      // A null id is a real request — it clears the mark — so this is
      // not guarded the way `openEvent` is.
      try { highlight(id); } catch { return false; }
      return true;
    },

    showChildEvent({ objectId, geometry }) {
      // Ordered so the camera is already moving while the panel
      // mounts, and so a host with no panel still gets the camera.
      const framed = geometry
        ? actions.fitEventArea({ geometry })
        : false;
      const marked = actions.highlightEvent({ objectId });
      const opened = actions.openEvent({ objectId });
      return framed || marked || opened;
    },

    showEventArea({ kind, objectId, geometry }) {
      if (!EVENT_AREA_KINDS.includes(kind)) return false;
      const framed = actions.fitEventArea({ geometry });
      // Marked as well as framed, because several of an event's areas
      // overlap and a camera alone does not say which one was asked
      // for. The map decides what the mark looks like.
      const marked = objectId ? actions.highlightEvent({ objectId }) : false;
      return framed || marked;
    },
  };

  return actions;
}

export default createMapActions;
